import React, { useEffect, useRef, useState } from 'react';
import { Check, Pencil, X, Sparkles } from 'lucide-react';

const CHIP_W = 340;

const IconBtn = ({ testId, label, onClick, cls, children }) => (
  <button type="button" data-testid={testId} aria-label={label} title={label} onClick={onClick}
    className={`h-7 w-7 inline-flex items-center justify-center rounded-md transition-colors ${cls}`}>{children}</button>
);

// WB-INK-001 — manual-mode suggestion: Convert to “Human”? — Accept | Edit | Dismiss (nothing else pops up)
export const InkSuggestionChip = ({ ink, canvasSize }) => {
  const { suggestion, accept, dismiss } = ink;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { setEditing(false); setValue(suggestion?.text || ''); }, [suggestion?.groupId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (editing) setTimeout(() => inputRef.current?.select(), 0); }, [editing]);

  useEffect(() => {
    if (!suggestion || editing) return undefined;
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'textarea' || tag === 'input') return;
      if (e.key === 'Enter') { e.preventDefault(); accept(); }
      else if (e.key === 'Escape') { e.preventDefault(); dismiss(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [suggestion, editing, accept, dismiss]);

  if (!suggestion) return null;
  const { box } = suggestion;
  const left = Math.max(8, Math.min(box.x, (canvasSize.w || CHIP_W + 16) - CHIP_W - 8));
  const below = box.y + box.h + 10;
  const top = canvasSize.h && below + 44 > canvasSize.h ? Math.max(8, box.y - 48) : below;

  return (
    <div data-testid="wb-ink-suggestion" data-confidence={suggestion.confidence}
      className="absolute z-20 flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs shadow-lg wb-chip-in bg-slate-900 text-white"
      style={{ left, top, maxWidth: CHIP_W }} onPointerDown={(e) => e.stopPropagation()}>
      <Sparkles className="h-3.5 w-3.5 text-amber-300 shrink-0" />
      {editing ? (
        <form className="flex items-center gap-1.5" onSubmit={(e) => { e.preventDefault(); accept(value); }}>
          <input
            ref={inputRef}
            data-testid="wb-ink-edit-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setEditing(false); } }}
            className="h-7 w-44 rounded-md bg-slate-800 border border-slate-600 px-2 text-xs text-white outline-none focus:border-blue-400"
          />
          <IconBtn testId="wb-ink-edit-save" label="Convert with this text" onClick={() => accept(value)} cls="bg-emerald-500 hover:bg-emerald-400 text-white"><Check className="h-4 w-4" /></IconBtn>
          <IconBtn testId="wb-ink-edit-cancel" label="Back" onClick={() => setEditing(false)} cls="text-slate-300 hover:bg-slate-700"><X className="h-4 w-4" /></IconBtn>
        </form>
      ) : (
        <>
          <span className="truncate" data-testid="wb-ink-suggestion-text">
            Convert to <span className="font-semibold">“{suggestion.text}”</span>?
          </span>
          {suggestion.confidence !== 'high' && (
            <span data-testid="wb-ink-confidence" className="shrink-0 rounded-full bg-amber-400/20 text-amber-200 px-1.5 py-px text-[10px] font-medium">
              {suggestion.confidence === 'low' ? 'Low confidence' : 'Check spelling'}
            </span>
          )}
          <span className="ml-1 flex items-center gap-0.5 shrink-0">
            <IconBtn testId="wb-ink-accept" label="Accept (Enter)" onClick={() => accept()} cls="bg-emerald-500 hover:bg-emerald-400 text-white"><Check className="h-4 w-4" /></IconBtn>
            <IconBtn testId="wb-ink-edit" label="Edit text before converting" onClick={() => setEditing(true)} cls="text-slate-200 hover:bg-slate-700"><Pencil className="h-3.5 w-3.5" /></IconBtn>
            <IconBtn testId="wb-ink-dismiss" label="Dismiss (Esc)" onClick={dismiss} cls="text-slate-300 hover:bg-slate-700"><X className="h-4 w-4" /></IconBtn>
          </span>
        </>
      )}
    </div>
  );
};
